import React, { forwardRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { FormFieldContext } from '../FormField/FormFieldContext'

/**
 * Bronson Input component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-input.html
 *
 * @param {string} [addonClassName] - Additional class modifiers on the addon element.
 * @param {string} [addonElement='span'] - The addon element type.
 * @param {string} [addonIcon] - The addon icon.
 * @param {string} [addonText] - The addon text label.
 * @param {string} [addonType] - The addon element type.
 * @param {string} [className] - Additional classnames for the wrapping `<div>` element.
 * @param {string} [defaultValue] - The initial value for the Input.
 * @param {object} [divProps] - Other props passed to the wrapping `<div>` element.
 * @param {boolean} [error] - Render the Input as invalid.
 * @param {boolean} [ghost] - Render the Input as non-visible input (w/o border etc.).
 * @param {string} [id] - The `<input>` element `[id]` attribute.
 * @param {string} [modifierStates] - Additional CSS classes for the `<input>` element.
 * @param {boolean} [noBackground] - Remove `background-color` from the input.
 * @param {function} [onChange] - Callback function when the input value changes.
 * @param {boolean} [reversed] - Render the Input in reversed order.
 * @param {boolean} [stateIcon] - Optional icon to convey the current state.
 * @param {boolean} [success] - Render the Input as valid.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {string} [type] - The `<input>` element `[type]` attribute, e.g. `text` or `number`.
 * @param {object} [otherProps] - Other props passed to the component element.
 *                                Replaces 'attr' from Bronson template.
 * @returns {JSX.Element} - The Input component.
 * @constructor
 */

export const Input = forwardRef(
  (
    {
      addonClassName,
      addonElement = 'span',
      addonIcon,
      addonText,
      addonType,
      className,
      defaultValue,
      divProps,
      error,
      ghost,
      id,
      modifierStates,
      noBackground,
      onChange,
      reversed,
      stateIcon,
      success,
      testId,
      type,
      ...otherProps
    },
    ref
  ) => {
    const CustomElement = `${addonElement}`

    /**
     * Track internal state via the wrapping {@link FormFieldContext}.
     * @type {object}
     */
    const context = React.useContext(FormFieldContext)

    /**
     * Hook to set active/inactive states on the {@link Input}.
     */
    useEffect(() => {
      if (context?.setElementActive) {
        context.setElementActive(!!defaultValue)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue])

    const divClassNameList = classNames(
      'c-input',
      {
        [`c-input--${type} `]: type,
        // Main class modifier convenience prop extension.
        'c-input--ghost': ghost, // Convenience prop from Bronson variants.
        'c-input--reversed ': reversed, // Convenience prop from Bronson variants.
      },
      className
    ).trim()

    const inputClassNameList = classNames('c-input__input', {
      'is-success': success,
      'is-error': error,
      [`${modifierStates} `]: modifierStates,
    }).trim()

    function renderIfStateIcon() {
      if (stateIcon) {
        return <i className="c-input__state-icon" aria-hidden="true" role="img" />
      }
      return null
    }

    const spanClassNameList = classNames(
      'c-input__addon',
      {
        'c-input__addon--no-background': noBackground, // Convenience prop from Bronson variants.
      },
      addonClassName
    ).trim()

    const iClassNameList = classNames('c-icon', [`c-icon--[${addonIcon}]`]).trim()

    function renderIfInputAddonIconOrInputAddonText() {
      if (addonIcon || addonText) {
        return (
          <CustomElement
            className={spanClassNameList}
            type={addonType} // applies condition
          >
            {addonIcon ? (
              <>
                <i className={iClassNameList} aria-hidden="true" role="img" />
              </>
            ) : (
              addonText
            )}
          </CustomElement>
        )
      }
      return null
    }

    return (
      <div className={divClassNameList} {...divProps}>
        <input
          ref={ref}
          data-testid={testId}
          className={inputClassNameList}
          id={id}
          type={`${type || 'text'}`}
          aria-describedby={error && `error-${id}`}
          onChange={(event) => [
            onChange && onChange(event),
            context.setElementActive && context.setElementActive(!!event.target.value),
          ]}
          defaultValue={defaultValue}
          {...otherProps}
        />
        {renderIfStateIcon()}
        {renderIfInputAddonIconOrInputAddonText()}
      </div>
    )
  }
)

Input.propTypes = {
  addonClassName: PropTypes.string, // Bronson template: 'input-addon-modifiers'.
  addonElement: PropTypes.string, // Default is `span`, but you can use `button`.
  addonIcon: PropTypes.string, // Bronson template: 'input-addon-icon'.
  addonText: PropTypes.node, // Bronson template: 'input-addon-text'.
  addonType: PropTypes.string, // Bronson template: 'input-addon-type'.
  className: PropTypes.string, // Bronson template: 'input-modifiers'.
  defaultValue: PropTypes.string,
  divProps: PropTypes.oneOfType([PropTypes.object]),
  error: PropTypes.bool, // Bronson template: 'error'.
  id: PropTypes.string, // Bronson template: 'id'.
  modifierStates: PropTypes.string, // Bronson template: 'modifier-states'.
  onChange: PropTypes.func,
  stateIcon: PropTypes.bool, // Bronson template: 'has-state-icon'.
  success: PropTypes.bool, // Bronson template: 'success'.
  type: PropTypes.string, // Bronson template: 'type'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  /* Convenience props */
  ghost: PropTypes.bool, // Convenience prop for `.c-input--ghost` (Bronson template: 'input-modifiers').
  noBackground: PropTypes.bool, // Convenience prop for `.c-input__addon--no-background` (Bronson template: 'input-modifiers').
  reversed: PropTypes.bool, // Convenience prop for `.c-input--reversed` (Bronson template: 'input-modifiers').
}
